import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CustomerAnalyticsSummarySchema, type CustomerAnalyticsSummary } from '@inktap/contracts/customer-analytics';

import { request } from '../lib/api';
import { useSession } from '../session';
import { TapActionIcon } from '../features/tap-builder/TapActionIcon';
import type { TapActionType } from '../lib/tapDocument';
import './customer-analytics-contacts.css';

const formatDay = (value: string) => new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`));

type OpeningMethod = 'nfc' | 'qr' | 'link';

function OpeningMethodIcon({ method }: { method: OpeningMethod }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    {method === 'nfc' ? <><path d="M8 9a4 4 0 0 1 0 6M11 6a8 8 0 0 1 0 12M14 3a12 12 0 0 1 0 18" /><circle cx="5" cy="12" r="1" fill="currentColor" stroke="none" /></>
      : method === 'qr' ? <><rect x="4" y="4" width="6" height="6" rx=".5" /><rect x="14" y="4" width="6" height="6" rx=".5" /><rect x="4" y="14" width="6" height="6" rx=".5" /><path d="M14 14h2v2h-2zM19 14v3M14 20h3M20 20h.01" /></>
      : <><path d="M10 13.5 14 9.5M8.5 16.5l-1 1a3.5 3.5 0 0 1-5-5l3-3a3.5 3.5 0 0 1 5 0M15.5 7.5l1-1a3.5 3.5 0 0 1 5 5l-3 3a3.5 3.5 0 0 1-5 0" /></>}
  </svg>;
}

type MetricIconKind = 'opens' | 'actions' | 'visitor-days';

function MetricIcon({ kind }: { kind: MetricIconKind }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    {kind === 'opens' ? <><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" /><circle cx="12" cy="12" r="2.5" /></>
      : kind === 'actions' ? <path d="M5 3v14l4-4 3 7 3-1-3-7h6z" />
      : <><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16M8 14h3M8 17h3M14 14h2" /></>}
  </svg>;
}

const actionCategory = (type: string | null): { label: string; icon: TapActionType | null } => {
  switch (type) {
    case 'download-card': return { label: 'Contact download', icon: type };
    case 'booking': return { label: 'Booking', icon: type };
    case 'service-book': return { label: 'Service booking', icon: type };
    case 'website': return { label: 'Website', icon: type };
    case 'social': return { label: 'Social', icon: type };
    case 'file': return { label: 'File', icon: type };
    case 'custom': return { label: 'Custom action', icon: type };
    default: return { label: type || 'Unspecified action', icon: null };
  }
};

function AnalyticsPage() {
  const { session } = useSession();
  const observed = useRef({ session, version: 0 });
  if (observed.current.session !== session) {
    observed.current = { session, version: observed.current.version + 1 };
  }
  return session ? <AnalyticsWorkspace key={observed.current.version} /> : null;
}

function AnalyticsWorkspace() {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [summary, setSummary] = useState<CustomerAnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const requestGeneration = useRef(0);

  const load = useCallback(async () => {
    const generation = ++requestGeneration.current;
    setLoading(true);
    setError(null);
    try {
      const response = await request<unknown>('/me/analytics?days=30');
      if (generation !== requestGeneration.current) return;
      setSummary(CustomerAnalyticsSummarySchema.parse(response));
    } catch (err: unknown) {
      if (generation !== requestGeneration.current) return;
      setSummary(null);
      const data = err && typeof err === 'object' && 'data' in err ? err.data : null;
      const message = data && typeof data === 'object' && 'message' in data ? data.message : null;
      setError(typeof message === 'string' ? message : 'Analytics is unavailable right now.');
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    return () => { requestGeneration.current += 1; };
  }, [load]);

  const daily = useMemo(() => [...(summary?.daily ?? [])].sort((a, b) => a.date.localeCompare(b.date)), [summary]);
  const peak = Math.max(1, ...daily.flatMap((entry) => [entry.taps, entry.actionClicks]));
  const firstDay = daily[0] ? Date.parse(`${daily[0].date}T00:00:00Z`) : 0;
  const lastDay = daily.length ? Date.parse(`${daily[daily.length - 1].date}T00:00:00Z`) : 0;
  const point = (date: string, value: number) => {
    const x = lastDay === firstDay ? 300 : 8 + (Date.parse(`${date}T00:00:00Z`) - firstDay) / (lastDay - firstDay) * 584;
    return { x, y: 168 - value / peak * 152 };
  };
  const actionPeak = Math.max(0, ...(summary?.topActions.map((action) => action.clicks) ?? []));
  const actionLabelCounts = new Map<string, number>();
  summary?.topActions.forEach((action) => {
    const label = actionCategory(action.actionType).label;
    actionLabelCounts.set(label, (actionLabelCounts.get(label) ?? 0) + 1);
  });
  const sourceTotal = summary ? summary.totals.nfcTaps + summary.totals.qrScans + summary.totals.linkOpens : 0;
  const sourcesMatch = Boolean(summary && Number.isSafeInteger(sourceTotal) && sourceTotal === summary.totals.taps);
  const hasSourceShares = sourcesMatch && sourceTotal > 0;
  const nfcEnd = hasSourceShares && summary ? (summary.totals.nfcTaps / sourceTotal) * 100 : 0;
  const qrEnd = hasSourceShares && summary ? ((summary.totals.nfcTaps + summary.totals.qrScans) / sourceTotal) * 100 : 0;

  return (
    <div className="dashboard-page analytics-page">
      <header className="dashboard-hero dashboard-hero--workspace">
        <div className="dashboard-hero__head">
          <h1 ref={headingRef} tabIndex={-1}>Card analytics</h1>
          <p className="dashboard-hero__lead">See how people use your smart business card and which actions turn attention into connections.</p>
        </div>
        <button type="button" className="button-secondary" onClick={() => void load()} disabled={loading}>Refresh</button>
      </header>

      {loading ? <section className="dashboard-section"><p className="helper-text" role="status">Loading your card activity…</p></section> : null}
      {error ? (
        <section className="dashboard-section analytics-page__empty">
          <h2>Analytics is unavailable</h2>
          <p>{error}</p>
          <div className="analytics-page__recovery-actions">
            <button type="button" className="btn-primary" onClick={() => {
              headingRef.current?.focus();
              void load();
            }}>Retry analytics</button>
            <Link className="button-secondary" to="/plan-billing">View plans</Link>
          </div>
        </section>
      ) : null}
      {!loading && summary ? (
        <>
          <section className="analytics-page__metrics" aria-label="Last 30 days">
            {([
              ['opens', 'Total page opens', summary.totals.taps, 'QR scans, NFC taps, and direct link opens'],
              ['actions', 'Action clicks', summary.totals.actionClicks, 'Clicks on your contact and links'],
              ['visitor-days', 'Visitor days', summary.totals.visitorDays, 'Privacy-respecting daily visitors']
            ] as const).map(([kind, label, count, description]) => (
              <article key={kind}><span className="analytics-page__metric-icon"><MetricIcon kind={kind} /></span>
                <span className="analytics-page__metric-label">{label}</span><strong>{count}</strong><p>{description}</p>
              </article>
            ))}
          </section>
          <div className="analytics-page__analysis-grid">
          <section className="dashboard-section analytics-page__chart">
            <div className="dashboard-section__head"><div><h2>Page opens and actions</h2><p className="dashboard-section__subhead">Daily page opens and action clicks over the last 30 days.</p></div></div>
            {daily.length ? (
              <>
                <div className="analytics-page__legend" aria-hidden="true"><span>Page opens</span><span>Action clicks</span></div>
                <div className="analytics-page__trend" role="img" aria-label="Daily activity chart. Exact values are available in the daily breakdown.">
                  <div className="analytics-page__scale" aria-hidden="true"><span>{peak}</span><span>0</span></div>
                  <svg viewBox="0 0 600 180" preserveAspectRatio="none" aria-hidden="true">
                    {[16, 54, 92, 130, 168].map(y => <line key={y} x1="8" x2="592" y1={y} y2={y} className="analytics-page__gridline" />)}
                    <polyline className="analytics-page__line analytics-page__line--opens" points={daily.map(entry => { const p = point(entry.date, entry.taps); return `${p.x},${p.y}`; }).join(' ')} />
                    <polyline className="analytics-page__line analytics-page__line--actions" points={daily.map(entry => { const p = point(entry.date, entry.actionClicks); return `${p.x},${p.y}`; }).join(' ')} />
                    {daily.map((entry, index) => { const opens = point(entry.date, entry.taps); const actions = point(entry.date, entry.actionClicks); return (
                      <g key={`${entry.date}-${index}`}>
                        <circle className="analytics-page__point analytics-page__point--opens" cx={opens.x} cy={opens.y} r="3"><title>{formatDay(entry.date)}: {entry.taps} opens</title></circle>
                        <circle className="analytics-page__point analytics-page__point--actions" cx={actions.x} cy={actions.y} r="3"><title>{formatDay(entry.date)}: {entry.actionClicks} action clicks</title></circle>
                      </g>
                    ); })}
                  </svg>
                </div>
                <div className="analytics-page__dates" aria-hidden="true"><span>{formatDay(daily[0].date)}</span><span>{formatDay(daily[daily.length - 1].date)}</span></div>
                <details className="analytics-page__breakdown">
                  <summary>View daily breakdown</summary>
                  <p className="analytics-page__table-hint">Scroll to see all dates and columns.</p>
                  <div className="analytics-page__table-scroll" role="region" aria-label="Daily activity table" tabIndex={0}>
                    <table><caption>Daily activity, UTC dates</caption>
                      <thead><tr><th scope="col">Date</th><th scope="col">Opens</th><th scope="col">QR</th><th scope="col">NFC</th><th scope="col">Link</th><th scope="col">Actions</th></tr></thead>
                      <tbody>{daily.map((entry, index) => <tr key={`${entry.date}-${index}`}><th scope="row"><time dateTime={entry.date}>{formatDay(entry.date)}</time></th><td>{entry.taps}</td><td>{entry.qrScans}</td><td>{entry.nfcTaps}</td><td>{entry.linkOpens}</td><td>{entry.actionClicks}</td></tr>)}</tbody>
                    </table>
                  </div>
                </details>
              </>
            ) : <p className="helper-text">Daily activity will appear when your Tap Page is ready.</p>}
          </section>
          <section className="dashboard-section analytics-page__sources" aria-labelledby="analytics-sources-title">
            <div className="dashboard-section__head"><div><h2 id="analytics-sources-title">How people opened your page</h2><p className="dashboard-section__subhead">Opening methods in the last 30 days.</p></div></div>
            <div className="analytics-page__source-content">
              <div className="analytics-page__source-mark" aria-hidden="true" style={{ background: hasSourceShares ? `conic-gradient(#0754b8 0% ${nfcEnd}%, #1685f5 ${nfcEnd}% ${qrEnd}%, #83bbef ${qrEnd}% 100%)` : '#dceeff' }}><span>{summary.totals.taps}</span><small>page opens</small></div>
              <ul className="analytics-page__source-list">
                {([
                  ['nfc', 'NFC taps', summary.totals.nfcTaps, 'Taps of the programmed card'],
                  ['qr', 'QR scans', summary.totals.qrScans, 'Scans of the printed QR code'],
                  ['link', 'Link opens', summary.totals.linkOpens, 'Direct, copied, or shared tap-page links']
                ] as const).map(([method, label, count, description]) => (
                  <li key={method}><span className={`analytics-page__source-icon analytics-page__source-icon--${method}`}><OpeningMethodIcon method={method} /></span>
                    <span className="analytics-page__source-label">{label}</span><strong>{count}</strong>
                    <span className="analytics-page__source-share">{hasSourceShares ? `${Math.round(count / sourceTotal * 100)}%` : <><span aria-hidden="true">-</span><span className="sr-only">No percentage available</span></>}</span>
                    <small className="analytics-page__source-description">{description}</small>
                  </li>
                ))}
              </ul>
            </div>
            {!sourcesMatch ? <p className="analytics-page__source-warning" role="status">Opening-method percentages are unavailable because the reported totals do not match. Refresh to try again.</p>
              : <p className="analytics-page__source-note">{hasSourceShares ? 'Percentages are rounded to the nearest whole number.' : 'No page opens recorded for this period.'}</p>}
          </section>
          <section className="dashboard-section analytics-page__top-actions">
            <div className="dashboard-section__head"><div><h2>Top actions</h2><p className="dashboard-section__subhead">Action types people use most after opening your card. Labels are not included in these analytics. Bars compare each action with the most-clicked action.</p></div></div>
            {summary.topActions.length ? <ol className="analytics-page__actions">{summary.topActions.map((action) => {
              const category = actionCategory(action.actionType);
              return <li key={JSON.stringify([action.actionId, action.actionType])}>
                <span className="analytics-page__action-icon" aria-hidden="true">{category.icon ? <TapActionIcon type={category.icon} /> : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 3v14l4-4 3 7 3-1-3-7h6z" /></svg>}</span>
                <span className="analytics-page__action-name">{category.label}{(actionLabelCounts.get(category.label) ?? 0) > 1 ? <small>Action ID: {action.actionId}</small> : null}</span>
                <span className="analytics-page__action-track" aria-hidden="true"><span style={{ width: `${actionPeak ? (action.clicks / actionPeak) * 100 : 0}%` }} /></span>
                <strong>{action.clicks} {action.clicks === 1 ? 'click' : 'clicks'}</strong>
              </li>;
            })}</ol> : <p className="helper-text">No action clicks yet. Your activity will appear here as people use your card.</p>}
          </section>
          <section className="dashboard-section analytics-page__next-steps">
            <h2>Keep connecting</h2>
            <p>Share your Tap Page and review the contacts you receive.</p>
            <Link className="btn-primary" to="/tap">View your Tap Page</Link>
            <Link className="button-secondary" to="/contacts">Go to contacts</Link>
          </section>
          </div>
        </>
      ) : null}
    </div>
  );
}

export default AnalyticsPage;
